import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from 'axios';
import { Result, Button, Spin, message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTheme } from '@/context/theme';

const HandbookAcknowledgmentHandler = () => {
  const { token } = useParams();
  const history = useHistory();
  const { isDarkMode } = useTheme();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('processing');
  const [errorMsg, setErrorMsg] = useState('');
  const [handbookInfo, setHandbookInfo] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/handbook/acknowledge/${token}?verify=true`);
        if (response.data.success) {
          setHandbookInfo(response.data.handbookInfo);
          setStatus(response.data.alreadyAcknowledged ? 'success' : 'ready');
        }
      } catch (error) {
        console.error('Token validation error:', error);
        setStatus(error.response?.data?.expired ? 'expired' : 'error');
        setErrorMsg(error.response?.data?.message || 'Invalid or expired acknowledgment link');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      validateToken();
    } else {
      setStatus('error');
      setErrorMsg('Missing acknowledgment token');
      setLoading(false);
    }
  }, [token]);

  const handleConfirm = async () => {
    try {
      setProcessing(true);
      const response = await axios.get(`/handbook/acknowledge/${token}`);
      if (response.data.success) {
        setHandbookInfo(response.data.handbookInfo);
        setStatus('success');
        message.success('Acknowledgment recorded successfully!');
      }
    } catch (error) {
      if (error.response?.data?.expired) {
        setStatus('expired');
        setErrorMsg(error.response.data.message);
      } else {
        message.error(error.response?.data?.message || 'Failed to acknowledge');
      }
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-PK', {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  // ─── Theme tokens ──────────────────────────────────────────────────────────
  const BRAND_GRADIENT = 'linear-gradient(135deg, #00aaf1 0%, #03488a 100%)';
  const SUCCESS_GRADIENT = 'linear-gradient(135deg, #00c853 0%, #1b5e20 100%)';
  const WARN_COLOR = '#faad14';

  const bg       = isDarkMode ? '#000000' : '#F1F5F9';
  const cardBg   = isDarkMode ? '#111111' : '#ffffff';
  const border   = isDarkMode ? '#1e293b' : '#e2e8f0';
  const text      = isDarkMode ? '#f1f5f9' : '#0f172a';
  const textMuted = isDarkMode ? '#94a3b8' : '#64748b';
  const innerBg  = isDarkMode ? '#1a1a1a' : '#f8fafc';

  const pageStyle = {
    minHeight: '100vh',
    backgroundColor: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    fontFamily: "'Nunito', sans-serif",
  };

  const cardStyle = {
    maxWidth: 480,
    width: '100%',
    backgroundColor: cardBg,
    borderRadius: 16,
    boxShadow: isDarkMode
      ? '0 8px 32px rgba(0,0,0,0.6)'
      : '0 8px 32px rgba(0,0,0,0.10)',
    overflow: 'hidden',
    border: `1px solid ${border}`,
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={pageStyle}>
        <Spin size="large" tip="Verifying your link..." />
      </div>
    );
  }

  // ── Already acknowledged ───────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ background: SUCCESS_GRADIENT, padding: '36px 32px 28px', textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: 52, color: '#fff', display: 'block', marginBottom: 10 }} />
            <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: 0, fontFamily: "'Nunito', sans-serif" }}>
              Acknowledgment Confirmed
            </h1>
          </div>
          <div style={{ padding: '24px 28px 28px' }}>
            <p style={{ color: text, fontSize: 14, lineHeight: 1.7, margin: '0 0 16px', textAlign: 'center' }}>
              You have successfully acknowledged the updated Employee Handbook.
            </p>
            {handbookInfo?.acknowledgmentDate && (
              <div style={{
                backgroundColor: innerBg, border: `1px solid ${border}`,
                borderRadius: 10, padding: '12px 16px', marginBottom: 20, textAlign: 'center',
              }}>
                <p style={{ color: textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>
                  Confirmed on
                </p>
                <p style={{ color: text, fontSize: 15, fontWeight: 700, margin: 0 }}>
                  {formatDate(handbookInfo.acknowledgmentDate)}
                </p>
              </div>
            )}
            <Button type="primary" block size="large"
              style={{ borderRadius: 10, height: 48, fontWeight: 700, background: BRAND_GRADIENT, border: 'none' }}
              onClick={() => history.push('/')}>
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Confirmation card ──────────────────────────────────────────────────────
  if (status === 'ready') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          {/* Header */}
          <div style={{ background: BRAND_GRADIENT, padding: '36px 32px 28px', textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: 48, color: '#fff', marginBottom: 12, display: 'block' }} />
            <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: 0, fontFamily: "'Nunito', sans-serif" }}>Final Step</h1>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, margin: '6px 0 0', fontWeight: 500 }}>
              Confirm your receipt of the handbook update
            </p>
          </div>

          {/* Body */}
          <div style={{ padding: '28px 32px' }}>
            <div style={{
              backgroundColor: innerBg, borderRadius: 12, padding: '18px 20px',
              marginBottom: 24, border: `1px solid ${border}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ color: textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Document
                </span>
                <span style={{
                  color: '#00aaf1', fontSize: 11, fontWeight: 700,
                  background: isDarkMode ? 'rgba(0,170,241,0.15)' : 'rgba(0,170,241,0.1)',
                  padding: '3px 10px', borderRadius: 6,
                }}>
                  v{handbookInfo?.version || '1'}
                </span>
              </div>
              <p style={{ color: text, fontWeight: 700, fontSize: 15, margin: '0 0 14px', wordBreak: 'break-word' }}>
                {handbookInfo?.fileName || 'Employee Handbook'}
              </p>
              {handbookInfo?.expiresAt && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                  <ClockCircleOutlined style={{ color: WARN_COLOR, fontSize: 13 }} />
                  <span style={{ color: WARN_COLOR, fontSize: 12, fontWeight: 600 }}>
                    Link expires: {formatDate(handbookInfo.expiresAt)}
                  </span>
                </div>
              )}
              <div style={{ borderTop: `1px solid ${border}`, paddingTop: 12 }}>
                <p style={{ color: textMuted, fontSize: 12, textAlign: 'center', fontStyle: 'italic', margin: 0, lineHeight: 1.6 }}>
                  By clicking below, you confirm that you have received the updated handbook and agree to abide by the company policies therein.
                </p>
              </div>
            </div>

            <Button
              type="primary" size="large" block loading={processing} onClick={handleConfirm}
              style={{
                height: 52, borderRadius: 12, fontWeight: 800, fontSize: 15,
                background: BRAND_GRADIENT, border: 'none',
                boxShadow: '0 4px 14px rgba(0,170,241,0.35)',
              }}
            >
              Confirm Acknowledgment
            </Button>

            <p style={{ color: textMuted, fontSize: 11, textAlign: 'center', marginTop: 14 }}>
              This action will be permanently recorded in your employee profile.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Expired ────────────────────────────────────────────────────────────────
  if (status === 'expired') {
    return (
      <div style={pageStyle}>
        <Result
          icon={<ClockCircleOutlined style={{ color: WARN_COLOR }} />}
          title={<span style={{ color: text, fontFamily: "'Nunito', sans-serif" }}>Link Expired</span>}
          subTitle={<span style={{ color: textMuted }}>{errorMsg}</span>}
          extra={[
            <Button key="contact" onClick={() => (window.location.href = 'mailto:hr.dymaxtech@gmail.com')}>
              Contact HR
            </Button>,
          ]}
          style={{ backgroundColor: 'transparent' }}
        />
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  return (
    <div style={pageStyle}>
      <Result
        status="error"
        icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
        title={<span style={{ color: text, fontFamily: "'Nunito', sans-serif" }}>Acknowledgment Failed</span>}
        subTitle={<span style={{ color: textMuted }}>{errorMsg}</span>}
        extra={[
          <Button type="primary" key="dashboard"
            style={{ background: BRAND_GRADIENT, border: 'none' }}
            onClick={() => history.push('/')}>
            Back to Home
          </Button>,
          <Button key="contact" onClick={() => (window.location.href = 'mailto:hr.dymaxtech@gmail.com')}>
            Contact HR
          </Button>,
        ]}
        style={{ backgroundColor: 'transparent' }}
      />
    </div>
  );
};

export default HandbookAcknowledgmentHandler;
